/**
 * Microsoft Teams / Microsoft 365 host integration — the "mot-r" personal tab.
 *
 * The tab (in Teams, Outlook and the Microsoft 365 app) loads the app in an iframe at
 * "/?host=teams". The flag is remembered for the session because routing drops the query.
 *
 * Sign-in cannot redirect inside the iframe, and Teams desktop blocks window.open, so the tab
 * asks Teams to open its own auth popup (authentication.authenticate) on "/?popup=login&host=teams".
 * That popup runs the normal OIDC redirect flow, then hands the signed-in user back with
 * authentication.notifySuccess — the same payload the NetDocuments popup posts to its opener,
 * because the tab's storage is partitioned from the popup's.
 */
import { app, authentication } from '@microsoft/teams-js';
import { discoverFirmByEmail, isInOutlookTaskPane, type ExtensionFirm } from './extensionHost';
import { extractTenantFromUrl } from '@/tenant/tenantResolver';

const TAB_KEY = 'motr.host.teams';
const AUTH_POPUP_KEY = 'motr.teams.authPopup';
const PROMPT_KEY = 'motr.teams.prompt';
// In the tab's (partitioned) localStorage: the user signed out, so the next sign-in must ask
// for the password even though mot-r's login cookie in the popup is still valid.
const SIGNED_OUT_KEY = 'motr.teams.signedOut';

export interface TeamsAuthResult {
  oidcStorageKey?: string;
  oidcUserJson?: string | null;
}

const readSession = (key: string): boolean => {
  try { return sessionStorage.getItem(key) === '1'; } catch { return false; }
};

const writeSession = (key: string, on: boolean) => {
  try {
    if (on) sessionStorage.setItem(key, '1');
    else sessionStorage.removeItem(key);
  } catch { /* storage blocked: the URL flag still works for this load */ }
};

const params = new URLSearchParams(window.location.search);
const isPopupStart = params.get('popup') === 'login' && params.get('host') === 'teams';

// Captured at import time, before the router or the auth callback rewrites the URL.
if (isPopupStart) {
  writeSession(AUTH_POPUP_KEY, true);
  try {
    if (params.get('prompt') === 'login') sessionStorage.setItem(PROMPT_KEY, 'login');
    else sessionStorage.removeItem(PROMPT_KEY);
  } catch { /* ignore */ }
} else if (params.get('host') === 'teams') {
  writeSession(TAB_KEY, true);
}

/**
 * Running as the mot-r tab inside Teams, Outlook or the Microsoft 365 app. The flag is in session
 * storage, which Outlook's add-in pane shares with the tab (same window, same address), so the
 * pane is excluded explicitly.
 */
export const isTeamsTab = (): boolean => readSession(TAB_KEY) && !isInOutlookTaskPane();

/** This window is the Teams sign-in popup (from its first load until the sign-in completes). */
export const isTeamsAuthPopup = (): boolean => isPopupStart || readSession(AUTH_POPUP_KEY);

/** True only on the popup's first load, before the OIDC redirect. */
export const isTeamsAuthPopupStart = (): boolean => isPopupStart;

let initPromise: Promise<boolean> | null = null;

/** Connects to the Teams host. Resolves false (never rejects) when not hosted by Microsoft 365. */
export const initTeams = (): Promise<boolean> => {
  if (!initPromise) {
    initPromise = Promise.race([
      app.initialize().then(() => true),
      new Promise<boolean>((resolve) => setTimeout(() => resolve(false), 5000)),
    ]).catch(() => false);
  }
  return initPromise;
};

/** The signed-in Microsoft 365 user's address (their UPN), or null when Teams does not give one. */
const getTeamsUserEmail = async (): Promise<string | null> => {
  if (!(await initTeams())) return null;
  try {
    const context = await app.getContext();
    const email = context.user?.userPrincipalName || context.user?.loginHint || '';
    return email.includes('@') ? email : null;
  } catch {
    return null;
  }
};

/**
 * The firm the tab signs in to. A firm's own address (e.g. demo-motr.initto.work) is that firm;
 * on the central host (e.g. mot-r.app) it comes from the Microsoft 365 user's email domain, the
 * same mapping the Outlook add-in and the Chrome extension use. Null when neither says.
 */
export const findTeamsFirm = async (apiUrl: string): Promise<ExtensionFirm | null> => {
  const hostTenant = extractTenantFromUrl();
  if (hostTenant) {
    try {
      const response = await fetch(`${apiUrl}/api/abp/multi-tenancy/tenants/by-name/${encodeURIComponent(hostTenant)}`, {
        credentials: 'omit',
        headers: { Accept: 'application/json' },
      });
      const found = response.ok
        ? ((await response.json()) as { success?: boolean; tenantId?: string; name?: string })
        : null;
      return found?.success && found.tenantId
        ? { tenantId: found.tenantId, name: found.name || hostTenant, tenantName: found.name || hostTenant }
        : null;
    } catch {
      return null;
    }
  }
  const email = await getTeamsUserEmail();
  return email ? discoverFirmByEmail(apiUrl, email) : null;
};

/**
 * Opens the Teams sign-in popup and resolves with the signed-in user for the tab's storage.
 * tenantId: the firm to sign in to ('' = the host account), when known.
 */
export const teamsSignIn = async (tenantId?: string | null): Promise<TeamsAuthResult> => {
  if (!(await initTeams())) {
    throw new Error('Microsoft Teams is not available.');
  }
  let signedOut = false;
  try { signedOut = localStorage.getItem(SIGNED_OUT_KEY) === '1'; } catch { /* ignore */ }
  const tenantParam = tenantId != null ? `&__tenant=${encodeURIComponent(tenantId)}` : '';
  const result = await authentication.authenticate({
    url: `${window.location.origin}/?popup=login&host=teams${tenantParam}${signedOut ? '&prompt=login' : ''}`,
    width: 600,
    height: 720,
  });
  try { localStorage.removeItem(SIGNED_OUT_KEY); } catch { /* ignore */ }
  return JSON.parse(result) as TeamsAuthResult;
};

/** Tab side: remember an explicit sign-out so the next sign-in asks for the password again. */
export const markTeamsSignedOut = (): void => {
  try { localStorage.setItem(SIGNED_OUT_KEY, '1'); } catch { /* ignore */ }
};

/** Popup side: the OIDC prompt the tab asked for ("login" after a sign-out), if any. */
export const teamsSignInPrompt = (): string | undefined => {
  try { return sessionStorage.getItem(PROMPT_KEY) || undefined; } catch { return undefined; }
};

/** Popup side: hands the signed-in user back to the tab and closes the popup. */
export const completeTeamsAuthPopup = async (payload: TeamsAuthResult): Promise<void> => {
  writeSession(AUTH_POPUP_KEY, false);
  if (await initTeams()) {
    authentication.notifySuccess(JSON.stringify(payload));
  }
};
